import {
  BadgeCategoriaConcreto,
  BadgeCumplimiento,
  FechaCR,
  GraficoResistenciaMuestra,
} from '@/components/dominio';
import { LogoAdelante } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurvaTeorica, useMuestra } from '@/hooks/useLaboratorio';
import { useUmbralesCumplimiento } from '@/hooks/useUmbrales';
import {
  type EvaluacionCumplimiento,
  evaluarEnsayoCumplimiento,
  evaluarMuestraCumplimiento,
  interpolarCurvaTeorica,
} from '@adelante/shared';
import { ArrowLeft, Printer } from 'lucide-react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';

/**
 * `/lab/muestras/:id/informe` — vista imprimible (PDF vía el navegador) del
 * informe de ensayo de resistencia de una muestra.
 *
 * Ruta TOP-LEVEL (fuera de LayoutPrincipal) para que no tenga el header/nav
 * de la app — así sale una hoja limpia. Sigue protegida por auth porque todo
 * el router vive dentro de <AuthenticatedTemplate> en App.tsx.
 *
 * Botón "Imprimir / Guardar PDF" → window.print(). El usuario elige
 * "Guardar como PDF" en el diálogo del navegador. Sin dependencias nuevas.
 */

const FACTOR_MPA_A_KGCM2 = 10.197;

const PRINT_CSS = `
@media print {
  @page { size: A4 portrait; margin: 14mm; }
  .no-print { display: none !important; }
  html, body { background: #fff !important; }
}
.informe-hoja { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

function fmt(n: number | null | undefined, dec: number): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

export function InformeMuestraPantalla() {
  const { id: idStr } = useParams<{ id: string }>();
  const idMuestra = idStr && /^\d+$/.test(idStr) ? Number(idStr) : null;

  const consulta = useMuestra(idMuestra);
  const curvaQ = useCurvaTeorica();
  const umbralesCumplimiento = useUmbralesCumplimiento();

  if (idMuestra === null) {
    return <p className="p-8 text-sm text-destructive">ID de muestra inválido.</p>;
  }

  if (consulta.isLoading) {
    return (
      <div className="mx-auto max-w-[820px] space-y-4 p-8">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (consulta.error || !consulta.data?.data) {
    return (
      <div className="space-y-4 p-8">
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.history.back()}
          className="gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <p className="text-sm text-destructive">
          {consulta.error?.message ??
            consulta.data?.error?.mensaje ??
            'No se pudo cargar la muestra.'}
        </p>
      </div>
    );
  }

  const muestra = consulta.data.data;
  const ensayos = [...muestra.ensayos].sort((a, b) => a.edad_dias - b.edad_dias);
  const curva = curvaQ.data?.data?.puntos ?? [];
  const fc = muestra.fc_objetivo;

  const evalMuestra: EvaluacionCumplimiento = evaluarMuestraCumplimiento(
    ensayos.map((e) => ({
      edad_dias: e.edad_dias,
      resistencia_kg_cm2_promedio: e.resistencia_kg_cm2_promedio,
    })),
    fc,
    curva,
    umbralesCumplimiento,
  );

  const hayMediciones = ensayos.some((e) => (e.mediciones?.length ?? 0) > 0);

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white">
      <style>{PRINT_CSS}</style>

      {/* Barra de acciones — no se imprime */}
      <div className="no-print sticky top-0 z-10 flex items-center gap-2 border-b bg-white px-4 py-3 shadow-sm">
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.history.back()}
          className="gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <Button size="sm" onClick={() => window.print()} className="gap-1.5">
          <Printer className="h-4 w-4" /> Imprimir / Guardar PDF
        </Button>
        <span className="ml-2 text-xs text-muted-foreground">
          En el diálogo de impresión elegí “Guardar como PDF” para descargar el informe.
        </span>
      </div>

      {/* Hoja del informe */}
      <div className="informe-hoja mx-auto my-6 max-w-[820px] bg-white p-10 text-zinc-900 shadow-md print:my-0 print:max-w-none print:p-0 print:shadow-none">
        {/* Encabezado */}
        <header className="flex items-start justify-between border-b-2 border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <LogoAdelante altura={28} />
            <div>
              <div className="text-lg font-bold tracking-wider">ADELANTE.</div>
              <div className="text-xs text-zinc-500">Laboratorio de Concreto</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
              Informe de ensayo
            </div>
            <div className="text-xs text-zinc-500">Resistencia a la compresión (ASTM C-39)</div>
          </div>
        </header>

        {/* Título + cumplimiento */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">
            Muestra <span className="font-mono">#{muestra.numero_muestra}</span>
          </h1>
          <BadgeCumplimiento evaluacion={evalMuestra} />
        </div>

        {/* Datos generales */}
        <section className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Dato label="Fecha de colado">
            <FechaCR fecha={muestra.fecha_colado} formato="largo" />
          </Dato>
          <Dato label="Actividad">{muestra.actividad_nombre}</Dato>
          <Dato label="Obra">
            {muestra.obra_works_no ? (
              <>
                <span className="font-mono">{muestra.obra_works_no}</span>
                {muestra.obra_display_name && (
                  <span className="text-zinc-500"> — {muestra.obra_display_name}</span>
                )}
              </>
            ) : (
              <span className="italic text-zinc-400">sin obra</span>
            )}
          </Dato>
          <Dato label="ID Casa / ubicación">
            {muestra.id_casa ?? <span className="italic text-zinc-400">—</span>}
          </Dato>
          <Dato label="Proveedor">{muestra.proveedor}</Dato>
          <Dato label="F'C objetivo">
            <span className="font-mono font-semibold">{fc} kg/cm²</span>
          </Dato>
          <Dato label="Tipo de concreto">
            <span className="inline-flex items-center gap-2">
              {muestra.tipo_concreto_display}
              <BadgeCategoriaConcreto categoria={muestra.categoria_concreto} />
            </span>
          </Dato>
          {muestra.codigo_interno_colada && (
            <Dato label="Colada vinculada">
              <span className="font-mono">#{muestra.codigo_interno_colada}</span>
            </Dato>
          )}
        </section>

        {/* Resultados de ensayos */}
        <section className="mt-6" style={{ breakInside: 'avoid' }}>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-700">
            Resultados de resistencia
          </h2>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-zinc-300 text-left">
                <Th>Edad (días)</Th>
                <Th>Fecha de prueba</Th>
                <Th className="text-right">Probetas</Th>
                <Th className="text-right">MPa prom.</Th>
                <Th className="text-right">kg/cm² prom.</Th>
                <Th className="text-right">% F'C</Th>
                <Th className="text-right">% teórico</Th>
                <Th>Resultado</Th>
              </tr>
            </thead>
            <tbody>
              {ensayos.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-4 text-center text-zinc-400">
                    Sin ensayos registrados.
                  </td>
                </tr>
              )}
              {ensayos.map((e) => {
                const kg = e.resistencia_kg_cm2_promedio;
                const pctFc = kg !== null ? (kg / fc) * 100 : null;
                const pctTeorico = interpolarCurvaTeorica(curva, e.edad_dias) * 100;
                const evalE = evaluarEnsayoCumplimiento(
                  { edad_dias: e.edad_dias, resistencia_kg_cm2_promedio: kg },
                  fc,
                  curva,
                  umbralesCumplimiento,
                );
                return (
                  <tr key={e.id} className="border-b border-zinc-200">
                    <Td className="font-medium">{e.edad_dias}</Td>
                    <Td>
                      {e.fecha_prueba ? (
                        <FechaCR fecha={e.fecha_prueba} formato="corto" />
                      ) : (
                        <span className="italic text-zinc-400">pendiente</span>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums">{e.cantidad_mediciones}</Td>
                    <Td className="text-right font-mono tabular-nums">
                      {fmt(e.resistencia_mpa_promedio, 1)}
                    </Td>
                    <Td className="text-right font-mono font-semibold tabular-nums">
                      {fmt(kg, 0)}
                    </Td>
                    <Td className="text-right tabular-nums">
                      {pctFc !== null ? `${fmt(pctFc, 0)}%` : '—'}
                    </Td>
                    <Td className="text-right tabular-nums text-zinc-500">
                      {pctTeorico > 0 ? `${fmt(pctTeorico, 0)}%` : '—'}
                    </Td>
                    <Td>
                      <BadgeCumplimiento evaluacion={evalE} />
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-zinc-500">
            % F'C = resistencia real / F'C objetivo. % teórico = resistencia esperada a esa edad
            según curva ASTM C-150. Cumple ≥ 95% del teórico · Marginal 85–95% · No cumple &lt; 85%.
          </p>
        </section>

        {/* Gráfico */}
        <section className="mt-6" style={{ breakInside: 'avoid' }}>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-700">
            Curva de resistencia
          </h2>
          <div className="rounded-md border border-zinc-200 p-2">
            <GraficoResistenciaMuestra ensayos={ensayos} fcObjetivo={fc} />
          </div>
        </section>

        {/* Detalle de probetas */}
        {hayMediciones && (
          <section className="mt-6" style={{ breakInside: 'avoid' }}>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-700">
              Detalle de probetas
            </h2>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-300 text-left">
                  <Th>Edad (días)</Th>
                  <Th className="text-right">Probeta</Th>
                  <Th className="text-right">MPa</Th>
                  <Th className="text-right">kg/cm²</Th>
                  <Th>Notas</Th>
                </tr>
              </thead>
              <tbody>
                {ensayos.flatMap((e) =>
                  (e.mediciones ?? []).map((m) => (
                    <tr key={m.id} className="border-b border-zinc-200">
                      <Td>{e.edad_dias}</Td>
                      <Td className="text-right tabular-nums">{m.orden}</Td>
                      <Td className="text-right font-mono tabular-nums">
                        {fmt(m.resistencia_mpa, 1)}
                      </Td>
                      <Td className="text-right font-mono tabular-nums">
                        {fmt(m.resistencia_mpa * FACTOR_MPA_A_KGCM2, 0)}
                      </Td>
                      <Td className="text-zinc-500">{m.notas ?? ''}</Td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </section>
        )}

        {/* Notas */}
        {muestra.notas && (
          <section className="mt-6" style={{ breakInside: 'avoid' }}>
            <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-zinc-700">
              Notas
            </h2>
            <p className="whitespace-pre-wrap text-sm text-zinc-700">{muestra.notas}</p>
          </section>
        )}

        {/* Pie */}
        <footer className="mt-10 flex items-end justify-between border-t border-zinc-200 pt-4 text-xs text-zinc-500">
          <div>
            Informe generado el{' '}
            {new Date().toLocaleDateString('es-CR', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
            .
          </div>
          <div className="text-center">
            <div className="mb-1 h-10 w-56 border-b border-zinc-400" />
            Responsable de laboratorio
          </div>
        </footer>
      </div>
    </div>
  );
}

function Dato({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-zinc-400">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function Th({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th className={`px-2 py-1.5 text-xs font-semibold text-zinc-600 ${className ?? ''}`}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={`px-2 py-1.5 align-top ${className ?? ''}`}>{children}</td>;
}
